// MenuPropertyPreview — slim, horizontal image-slider strip rendered
// underneath each property entry inside the lateral menu's Hotels &
// Bivouacs accordions. Lets guests visually identify each property
// without leaving the menu.
//
// Behaviour:
// - Pulls images from (in priority): CMS hero (about-hotel section),
//   brand-default `image`, CMS gallery, brand `gallery` array.
// - Always renders exactly 5 thumbnails per property (cycles the
//   available pool when fewer unique images exist, matching the
//   tile-padding pattern used by `buildAboutTiles`).
// - Clicking a thumbnail navigates straight to the hotel detail page —
//   same destination as the parent link, so the strip doubles as a
//   visual shortcut.
// - Pure HTML  loading="lazy" decoding="async"<img> with `onError` fallback so broken URLs drop out of
//   the strip silently.

import React, { useMemo, useState } from "react";
import { Link } from "react-router-dom";
import OptimizedImage from "@/components/OptimizedImage";
import {
  useHotelPropertyGalleries,
  getHeroUrl,
  getGalleryUrls,
} from "@/lib/useHotelPropertyGalleries";

const ABOUT_SECTION = "about-hotel";

const MenuPropertyPreview = ({ hotel, onNavigate, testIdPrefix = "menu-preview" }) => {
  const sections = useHotelPropertyGalleries(hotel.slug);
  const [broken, setBroken] = useState(() => new Set());

  const images = useMemo(() => {
    const out = [];
    const seen = new Set();
    const push = (url) => {
      if (!url || seen.has(url)) return;
      seen.add(url);
      out.push(url);
    };
    push(getHeroUrl(sections, ABOUT_SECTION));
    push(hotel.image);
    getGalleryUrls(sections, ABOUT_SECTION).forEach(push);
    (hotel.gallery || []).forEach(push);
    const pool = out.filter((u) => !broken.has(u));
    if (pool.length === 0) return [];
    // Always render exactly 5 tiles — cycle the pool when needed.
    return Array.from({ length: 5 }, (_, i) => pool[i % pool.length]);
  }, [sections, hotel.image, hotel.gallery, broken]);

  const markBroken = (url) =>
    setBroken((cur) => {
      if (cur.has(url)) return cur;
      const next = new Set(cur);
      next.add(url);
      return next;
    });

  if (images.length === 0) return null;

  return (
    <div
      className="mt-2 -ml-px flex gap-1.5 overflow-x-auto pb-1 [scrollbar-width:none] [&::-webkit-scrollbar]:hidden"
      data-testid={`${testIdPrefix}-${hotel.slug}`}
      // The menu preview thumbnails are miniatures of the hotel's
      // primary carousel images — editing those already updates the
      // menu strip. Opt every thumb out of AutoImageEditor so the
      // /admin/images tracker doesn't create duplicate slots.
      data-no-cms-edit="true"
    >
      {images.map((src, idx) => (
        <Link
          key={`${hotel.slug}-thumb-${idx}`}
          to={`/hotels/${hotel.slug}`}
          onClick={onNavigate}
          className="relative shrink-0 w-14 h-9 sm:w-16 sm:h-10 overflow-hidden rounded-[2px] bg-[#2C2621]/10 border border-[#2C2621]/10 hover:border-[#C16542] transition-colors"
          aria-label={`${hotel.name} — image ${idx + 1}`}
          tabIndex={-1}
          data-testid={`${testIdPrefix}-${hotel.slug}-thumb-${idx}`}
        >
          <OptimizedImage
            src={src}
            alt=""
            widths={[80, 160, 240]}
            sizes="80px"
            onError={() => markBroken(src)}
            className="absolute inset-0 w-full h-full object-cover"
            aria-hidden="true"
            objectFit="cover"
          />
        </Link>
      ))}
    </div>
  );
};

export default MenuPropertyPreview;
